import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { Database } from '../types/supabase';
import { Plus, Wand2, ChevronDown, Edit2, Trash2 } from 'lucide-react';
import Navigation from '../components/Navigation';
import ProjectForm from '../components/ProjectForm';
import { toast } from '../lib/toast';
import ProjectAICreator from '../components/ProjectAICreator';

type Project = Database['public']['Tables']['projects']['Row'];

interface ProjectsProps {
  navigate: (to: string) => void;
}

function Projects({ navigate }: ProjectsProps) {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingProject, setEditingProject] = useState<Project | null>(null);
  const [showAI, setShowAI] = useState(false);
  const [activeMenuId, setActiveMenuId] = useState<string | null>(null);
  const [activeMenuType, setActiveMenuType] = useState<'status' | 'priority' | null>(null);

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) return;

      const { data, error } = await supabase
        .from('projects')
        .select('*')
        .eq('user_id', session.user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setProjects(data || []);
    } catch (error) {
      console.error('Error fetching projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (projectId: string) => {
    try {
      const { error } = await supabase
        .from('projects')
        .delete()
        .eq('id', projectId);

      if (error) throw error;
      setProjects(projects.filter((project) => project.id !== projectId));
    } catch (error) {
      console.error('Error deleting project:', error);
    }
  };

  const handleStatusChange = async (projectId: string, newStatus: string) => {
    try {
      const { error } = await supabase
        .from('projects')
        .update({ status: newStatus })
        .eq('id', projectId);

      if (error) throw error;
      setProjects(projects.map(project =>
        project.id === projectId ? { ...project, status: newStatus } : project
      ));
      toast.success('Project status updated');
      closeMenus();
    } catch (error) {
      console.error('Error updating project status:', error);
      toast.error('Failed to update project status');
    }
  };

  const handlePriorityChange = async (projectId: string, newPriority: string) => {
    try {
      const { error } = await supabase
        .from('projects')
        .update({ priority: newPriority })
        .eq('id', projectId);

      if (error) throw error;
      setProjects(projects.map(project =>
        project.id === projectId ? { ...project, priority: newPriority } : project
      ));
      toast.success('Project priority updated');
      closeMenus();
    } catch (error) {
      console.error('Error updating project priority:', error);
      toast.error('Failed to update project priority');
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'Low':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
      case 'Normal':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-200';
      case 'High':
        return 'bg-orange-100 text-orange-800 dark:bg-orange-900/50 dark:text-orange-200';
      case 'Urgent':
        return 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Not Started':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
      case 'In Progress':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-200';
      case 'Testing':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900/50 dark:text-purple-200';
      case 'Awaiting Feedback':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-200';
      case 'Completed':
        return 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
    }
  };

  const toggleMenu = (projectId: string, menuType: 'status' | 'priority') => {
    if (activeMenuId === projectId && activeMenuType === menuType) {
      closeMenus();
    } else {
      setActiveMenuId(projectId);
      setActiveMenuType(menuType);
    }
  };

  const closeMenus = () => {
    setActiveMenuId(null);
    setActiveMenuType(null);
  };

  return (
    <div className="min-h-screen bg-white">
      <Navigation
        onSignOut={async () => await supabase.auth.signOut()}
        tasks={[]}
        currentView="all"
        onViewChange={() => {}}
        navigate={navigate}
      />

      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 sm:px-0">
          <div className="flex justify-between items-center mb-8">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Projects</h1>
            <div className="flex flex-col sm:flex-row gap-4 sm:items-center">
              <button
                onClick={() => setShowAI(!showAI)}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                <Wand2 className="h-5 w-5 mr-2" />
                Use Magic Tool
              </button>
              <button
                onClick={() => {
                  setEditingProject(null);
                  setShowForm(true);
                }}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                <Plus className="h-5 w-5 mr-2" />
                New Project
              </button>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="spinner">Loading...</div>
            </div>
          ) : showForm ? (
            <div className="bg-white dark:bg-gray-800 shadow sm:rounded-lg p-6">
              <ProjectForm
                onSuccess={() => {
                  setShowForm(false);
                  fetchProjects();
                }}
                onCancel={() => {
                  setShowForm(false);
                  setEditingProject(null);
                }}
                initialData={editingProject || undefined}
              />
            </div>
          ) : (
            <>
              {showAI && <ProjectAICreator onSuccess={fetchProjects} onCancel={() => setShowAI(false)} />}
              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                {projects.map((project) => (
                  <div
                    key={project.id}
                    className="bg-gradient-to-br from-white to-gray-50 dark:from-gray-800 dark:to-gray-900 shadow-lg rounded-xl p-6 group hover:shadow-xl transition-all duration-300"
                  >
                    <div className="flex flex-col space-y-3">
                      <div className="flex flex-col space-y-2">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white relative">
                          {project.name}
                        </h3>
                        {project.description && (
                          <p className="text-sm text-gray-600 dark:text-gray-300 line-clamp-2">
                            {project.description}
                          </p>
                        )}
                        <span className="text-sm font-medium text-gray-600 dark:text-gray-300 bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/30 dark:to-purple-900/30 px-3 py-1.5 rounded-lg inline-block border border-indigo-100 dark:border-indigo-800">
                          Customer: {project.agency_name}
                        </span>
                      </div>
                      <div className="flex flex-col space-y-3">
                        <div className="relative">
                          <button
                            onClick={() => toggleMenu(project.id, 'priority')}
                            className={`inline-flex items-center px-3 py-1.5 text-sm font-medium w-full justify-between ${getPriorityColor(project.priority)}`}
                          >
                            <span>Priority: {project.priority}</span>
                            <ChevronDown className="h-4 w-4 ml-2" />
                          </button>
                          {activeMenuId === project.id && activeMenuType === 'priority' && (
                            <div className="absolute left-0 right-0 mt-1 shadow-lg bg-white dark:bg-gray-800 z-10 rounded-md overflow-hidden">
                              <div className="py-1">
                                {['Low', 'Normal', 'High', 'Urgent'].map((priority) => (
                                  <button
                                    key={`${project.id}-${priority}`}
                                    onClick={() => handlePriorityChange(project.id, priority)}
                                    className={`block w-full text-left px-4 py-2 text-sm ${
                                      project.priority === priority
                                        ? 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white'
                                        : 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'
                                    }`}
                                  >
                                    {priority}
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                        <div className="relative">
                          <button
                            onClick={() => toggleMenu(project.id, 'status')}
                            className={`inline-flex items-center px-3 py-1.5 text-sm font-medium w-full justify-between ${getStatusColor(project.status || 'Not Started')}`}
                          >
                            <span>Status: {project.status || 'Not Started'}</span>
                            <ChevronDown className="h-4 w-4 ml-2" />
                          </button>
                          {activeMenuId === project.id && activeMenuType === 'status' && (
                            <div className="absolute left-0 right-0 mt-1 shadow-lg bg-white dark:bg-gray-800 z-10 rounded-md overflow-hidden">
                              <div className="py-1">
                                {['Not Started', 'In Progress', 'Testing', 'Awaiting Feedback', 'Completed'].map((status) => (
                                  <button
                                    key={`${project.id}-${status}`}
                                    onClick={() => handleStatusChange(project.id, status)}
                                    className={`block w-full text-left px-4 py-2 text-sm ${
                                      project.status === status
                                        ? 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white'
                                        : 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'
                                    }`}
                                  >
                                    {status}
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex justify-end space-x-2 pt-2 border-t border-gray-100 dark:border-gray-700">
                        <button
                          onClick={() => {
                            setEditingProject(project);
                            setShowForm(true);
                          }}
                          className="p-2 text-gray-400 hover:text-indigo-500 hover:bg-indigo-50 dark:hover:bg-indigo-900/50 rounded-lg transition-all duration-200"
                        >
                          <Edit2 className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleDelete(project.id)}
                          className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/50 rounded-lg transition-all duration-200"
                        >
                          <Trash2 className="h-5 w-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
                {projects.length === 0 && (
                  <div className="col-span-full text-center py-12">
                    <p className="text-gray-500 dark:text-gray-400">
                      No projects found. Create one to get started!
                    </p>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
}

export default Projects;